import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import {
    onAuthStateChanged,
    signOut,
    GoogleAuthProvider,
    signInWithPopup,
    signInWithPhoneNumber,
    RecaptchaVerifier
} from 'firebase/auth';
import { auth } from '../firebase';
import { useAgentSession, clearAgentSession } from '../utils/agentAuth';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

// One login for the whole site. A signed-in partner (travel agent) counts as logged in
// everywhere useAuth() is used (/chat, /search-form, booking pages, ...):
//   userProfile = { code: 2, isPartner: true, displayName, email, phoneNumber, ... }
//   idToken     = the partner session token (backends accept it for "webbot_agent:" ids)
// Signing in as a customer signs the partner out, and the other way round (agentAuth.js).
export const AuthProvider = ({ children }) => {
    const [firebaseProfile, setUserProfile] = useState({ code: 1 }); // 1: Initial/Loading, 2: Logged In, 3: Logged Out
    const [firebaseToken, setIdToken] = useState(null);
    const agentSession = useAgentSession();
    const partner = agentSession?.profile || null;
    const userProfile = partner
        ? {
            code: 2,
            isPartner: true,
            displayName: partner.business_name || partner.name || partner.email,
            photoURL: '/profileDefault.png',
            email: partner.email,
            phoneNumber: partner.phone || null,
            partnerId: partner.user_id,
        }
        : firebaseProfile;
    const idToken = partner ? agentSession.token : firebaseToken;
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isLoginLoading, setIsLoginLoading] = useState(false);
    const [mobileNumber, setMobileNumber] = useState("");
    const [otpSent, setOtpSent] = useState(false);
    const [otp, setOtp] = useState("");
    const [confirmationResult, setConfirmationResult] = useState(null);
    const [recaptchaContainerId, setRecaptchaContainerId] = useState(`recaptcha-container-${Date.now()}`);
    const [onLoginSuccess, setOnLoginSuccess] = useState(null);

    const recaptchaVerifierRef = useRef(null);

    // Partner signed in: chat and every other user_id-based call runs as "webbot_agent:<email>",
    // which the backends check against the partner token. Any anonymous chat moves over once.
    const partnerEmail = partner?.email ? String(partner.email).trim().toLowerCase() : "";
    useEffect(() => {
        if (!partnerEmail) return;
        const partnerUserId = `webbot_agent:${partnerEmail}`;
        const current = localStorage.getItem("routechef_user_id");
        if (current === partnerUserId) return;
        localStorage.setItem("routechef_user_id", partnerUserId);
        if (current && !/[@+]/.test(current) && !current.startsWith("webbot_agent:")) {
            migrateUserChatHistory(current, partnerUserId);   // anonymous chat only, never another account's
        }
    }, [partnerEmail]); // eslint-disable-line react-hooks/exhaustive-deps

    // Monitor auth state
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                const token = await user.getIdToken(true);
                setIdToken(token);
                setUserProfile({
                    code: 2,
                    displayName: user.displayName || user.phoneNumber,
                    photoURL: user.photoURL || '/profileDefault.png',
                    email: user.email,
                    phoneNumber: user.phoneNumber
                });

                // Set up token refresh
                const interval = setInterval(async () => {
                    const refreshedToken = await user.getIdToken(true);
                    setIdToken(refreshedToken);
                }, 50 * 60 * 1000);
                return () => clearInterval(interval);
            } else {
                setIdToken(null);
                setUserProfile({ code: 3 });
            }
        });

        return () => unsubscribe();
    }, []);

    const openLoginModal = (callback) => {
        if (typeof callback === 'function') {
            setOnLoginSuccess(() => callback);
        }
        setIsModalOpen(true);
    };
    const closeLoginModal = () => {
        setIsModalOpen(false);
        setMobileNumber("");
        setOtp("");
        setOtpSent(false);
        setConfirmationResult(null);
        setOnLoginSuccess(null);
        if (recaptchaVerifierRef.current) {
            try {
                recaptchaVerifierRef.current.clear();
            } catch (e) {
            }
            recaptchaVerifierRef.current = null;
        }
    };

    const handleSendOtp = async (phoneNumber) => {
        if (isLoginLoading) return;

        try {
            setIsLoginLoading(true);

            if (recaptchaVerifierRef.current) {
                try {
                    recaptchaVerifierRef.current.clear();
                } catch (e) {
                }
                recaptchaVerifierRef.current = null;
            }

            const newId = `recaptcha-container-${Date.now()}`;
            setRecaptchaContainerId(newId);

            // Give DOM time to update with new container ID
            await new Promise(resolve => setTimeout(resolve, 100));

            recaptchaVerifierRef.current = new RecaptchaVerifier(auth, newId, {
                size: "invisible",
                callback: () => {
                },
            });

            const confirmation = await signInWithPhoneNumber(auth, phoneNumber, recaptchaVerifierRef.current);
            setConfirmationResult(confirmation);
            setOtpSent(true);
            return { success: true };
        } catch (error) {
            console.error("Error sending OTP:", error);
            return { success: false, error };
        } finally {
            setIsLoginLoading(false);
        }
    };


    const handleGoogleLogin = async () => {
        if (isLoginLoading) return;

        try {
            setIsLoginLoading(true);
            const provider = new GoogleAuthProvider();
            const result = await signInWithPopup(auth, provider);
            clearAgentSession();   // a customer login replaces any partner session
            const user = result.user;
            const token = await user.getIdToken(true);

            const email = user.email || "";
            const newUserId = `webbot_trip:${email}`;

            console.log("🔐 Google login successful:", email);
            console.log("📧 New user_id:", newUserId);

            setIdToken(token);
            setUserProfile({
                code: 2,
                displayName: user.displayName || user.phoneNumber,
                photoURL: user.photoURL || './profileDefault.png',
                email: user.email,
                phoneNumber: user.phoneNumber
            });

            const oldUserId = localStorage.getItem("routechef_user_id") || `webbot_trip:temp`;
            await migrateUserChatHistory(oldUserId, newUserId);

            localStorage.setItem("routechef_user_id", newUserId);
            console.log("💾 Updated localStorage with new user_id");

            closeLoginModal();
            if (onLoginSuccess) {
                onLoginSuccess();
            }
            return { success: true };
        } catch (error) {
            console.error("Error with Google login:", error);
            return { success: false, error };
        } finally {
            setIsLoginLoading(false);
        }
    };
    const handleVerifyOtp = async (otpCode) => {
        if (isLoginLoading || !confirmationResult) return;

        try {
            setIsLoginLoading(true);
            const result = await confirmationResult.confirm(otpCode);
            clearAgentSession();   // a customer login replaces any partner session
            const user = result.user;
            const token = await user.getIdToken(true);

            // Extract phone and create new user_id
            const phone = user.phoneNumber || "";
            const newUserId = `webbot_trip:${phone}`;

            console.log("🔐 Phone OTP successful:", phone);
            console.log("📱 New user_id:", newUserId);

            setIdToken(token);
            setUserProfile({
                code: 2,
                displayName: user.displayName || user.phoneNumber,
                photoURL: user.photoURL || './profileDefault.png',
                email: user.email,
                phoneNumber: user.phoneNumber
            });

            // Get old user_id and migrate
            const oldUserId = localStorage.getItem("routechef_user_id") || `webbot_trip:temp`;
            await migrateUserChatHistory(oldUserId, newUserId);

            // Update localStorage with new user_id
            localStorage.setItem("routechef_user_id", newUserId);
            console.log("💾 Updated localStorage with new user_id");

            closeLoginModal();
            if (onLoginSuccess) {
                onLoginSuccess();
            }
            return { success: true };
        } catch (error) {
            console.error("❌ Error verifying OTP:", error);
            return { success: false, error };
        } finally {
            setIsLoginLoading(false);
        }
    };

    // Helper function to migrate chat history

    const handleLogout = async () => {
        try {
            if (partner) clearAgentSession();          // partner signed in: sign them out
            if (auth.currentUser) await signOut(auth);

            // Generate new random user_id for anonymous user
            const newRandomId = `webbot_trip:${Math.random().toString(36).slice(2, 10)}`;
            localStorage.setItem("routechef_user_id", newRandomId);
            console.log("🚪 Logged out. New anonymous user_id:", newRandomId);

            setUserProfile({ code: 3 });
            setIdToken(null);

            // Optionally reload to clear all state
            setTimeout(() => window.location.reload(), 500);

            return { success: true };
        } catch (error) {
            console.error("❌ Logout failed:", error);
            return { success: false, error };
        }
    };

    const migrateUserChatHistory = async (oldUserId, newUserId) => {
        if (oldUserId === newUserId) {
            console.log("⏭️  Same user_id, skipping migration");
            return;
        }

        try {
            console.log(`🔄 Migrating chat history: ${oldUserId} → ${newUserId}`);

            // Use the Lambda API endpoint
            const LAMBDA_BASE = "https://dw5wuw157e.execute-api.ap-south-1.amazonaws.com";
            const ACTIVE_API = `${LAMBDA_BASE}/development`;

            const response = await fetch(`${ACTIVE_API}/migrate`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    old_user_id: oldUserId,
                    new_user_id: newUserId,
                }),
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const result = await response.json();

            if (result.success) {
                console.log(`✅ Migration successful: ${result.migrated_count} messages migrated`);
                console.log(`   Old ID: ${oldUserId}`);
                console.log(`   New ID: ${newUserId}`);
                return result;
            } else {
                console.error("❌ Migration failed:", result.error);
                throw new Error(result.error || "Migration failed");
            }
        } catch (error) {
            console.error("❌ Migration error:", error);
            console.warn("⚠️  Chat migration failed, but login successful. User can continue.");
        }
    };

    const value = {
        userProfile,
        idToken,
        isPartner: !!partner,
        isModalOpen,
        isLoginLoading,
        mobileNumber,
        setMobileNumber,
        otpSent,
        otp,
        setOtp,
        recaptchaContainerId,
        openLoginModal,
        closeLoginModal,
        handleSendOtp,
        handleVerifyOtp,
        handleGoogleLogin,
        handleLogout
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
